import type { ViewDelta } from '@w/crdt'
import type { Block } from '@w/content-blocks'
import { indexViewBlocks, type BlockLocation, type PageBlock, type PageValue } from './doc-blocks'

export type BlockChange = { before: BlockLocation; after: BlockLocation }
export interface BlockDelta {
  inserted: BlockLocation[]
  deleted: BlockLocation[]
  moved: BlockChange[]
  updated: BlockChange[]
}

/** Visible membership is authoritative. A move cannot become a delete/insert side effect. */
export function classifyBlockDelta(delta: ViewDelta): BlockDelta {
  const paired = changedLocations(delta)
  if (paired) return classify(paired.before, paired.after, delta.movedIds)
  return classify(indexViewBlocks(delta.before), indexViewBlocks(delta.after), delta.movedIds)
}

/** `before` and `after` hold, in each view's document order, every block a change can be about. */
function classify(
  before: ReadonlyMap<string, BlockLocation>, after: ReadonlyMap<string, BlockLocation>, movedIds: readonly string[] | undefined,
): BlockDelta {
  const result: BlockDelta = { inserted: [], deleted: [], moved: [], updated: [] }
  const stable = new Set<string>()
  const lists = new Map<string, BlockLocation[]>()
  for (const location of after.values()) {
    const old = before.get(location.block.id)
    if (!old || old.parent !== location.parent || old.field !== location.field) continue
    const key = `${location.parent}\u0000${location.field}`
    const list = lists.get(key) ?? []
    list.push(location)
    lists.set(key, list)
  }
  for (const list of lists.values()) {
    for (const id of stableOrder(list, before)) stable.add(id)
  }
  for (const [id, location] of after) {
    const old = before.get(id)
    if (old) {
      if (movedIds ? movedIds.includes(id) : !stable.has(id)) result.moved.push({ before: old, after: location })
      if (old.block !== location.block && !same(old.block, location.block))
        result.updated.push({ before: old, after: location })
    } else result.inserted.push(location)
  }
  for (const [id, location] of before) if (!after.has(id)) result.deleted.push(location)
  return result
}

/**
 * The locations of the changed region of two frozen views that share every untouched object (crdt view patches):
 * every block of each list that is not the same array in both views, and the whole subtree of a block found in only
 * one of a list's two versions. An object found identical at the same place in both views holds no change, so it is
 * not walked (prosemirror-model Fragment.findDiffStart skips equal nodes the same way). Classifying these gives the
 * whole-index result: a block in an identical subtree is unchanged and keeps its order among its siblings. Undefined
 * (classify whole indexes) for unfrozen views, a moved id outside the walked region, or a walked block holding two
 * block lists under one field name (their siblings would share one stable-order group).
 */
function changedLocations(delta: ViewDelta): { before: Map<string, BlockLocation>; after: Map<string, BlockLocation> } | undefined {
  if (!Object.isFrozen(delta.before) || !Object.isFrozen(delta.after)) return undefined
  const after = new Map<string, BlockLocation>()
  const before = new Map<string, BlockLocation>()
  let ambiguous = false
  const walk = (value: PageValue, other: PageValue | undefined, path: string, parent: string, field: string,
    out: Map<string, BlockLocation>, whole: boolean): void => {
    if (!whole && value === other) return
    if (isList(value)) {
      const counterpart = !whole && other !== undefined && isList(other) ? other : undefined
      // Items identical at the same place from either end hold no change and keep their order, so they are neither
      // walked nor listed (prosemirror-model findDiffStart/findDiffEnd); a paste or Enter in a 2,000-block list walks
      // the changed middle only. The middle's ids appear only in the other list's middle (ids are unique).
      let start = 0
      let end = value.length
      if (counterpart) {
        const limit = Math.min(value.length, counterpart.length)
        while (start < limit && value[start] === counterpart[start]) start++
        while (end - start > 0 && counterpart.length - (value.length - end) - 1 >= start &&
          value[end - 1] === counterpart[counterpart.length - (value.length - end) - 1]) end--
      }
      const otherEnd = counterpart ? counterpart.length - (value.length - end) : 0
      const peers = counterpart && new Map(counterpart.slice(start, otherEnd).filter(item => isBlock(item)).map(block => [block.id, block]))
      for (let index = start; index < end; index++) {
        const item = value[index]
        if (isBlock(item)) {
          const blockPath = `${path}.${item.id}`
          out.set(item.id, { block: item, path: blockPath, list: path, index, parent, field })
          const peer = peers?.get(item.id)
          if (peer === item) continue
          if (peer && !whole && sharedFieldNames(item)) ambiguous = true
          walk(item, peer, blockPath, item.id, '', out, !peer)
        } else walk(item, whole ? undefined : counterpart?.[index], `${path}.[${index}]`, parent, `${field}.${index}`, out, whole || !counterpart)
      }
    } else if (value && typeof value === 'object') {
      const counterpart = !whole && other && typeof other === 'object' && !isList(other) ? other : undefined
      for (const [key, item] of Object.entries(value)) {
        walk(item, counterpart?.[key], path ? `${path}.${key}` : key, parent, key, out, whole || !counterpart)
      }
    }
  }
  const root = (view: unknown) => view as PageValue
  if (sharedFieldNames(root(delta.after)) || sharedFieldNames(root(delta.before))) return undefined
  walk(root(delta.after), root(delta.before), '', 'root', '', after, false)
  walk(root(delta.before), root(delta.after), '', 'root', '', before, false)
  if (ambiguous || delta.movedIds?.some(id => !after.has(id) || !before.has(id))) return undefined
  return { before, after }
}

/** Whether `owner` (a block or the page root) holds two block lists under one field name, its own fields only. */
function sharedFieldNames(owner: PageValue): boolean {
  const seen = new Set<string>()
  let shared = false
  const visit = (value: PageValue, field: string, top: boolean): void => {
    if (shared) return
    if (isList(value)) {
      if (value.some(item => isBlock(item))) {
        if (seen.has(field)) shared = true
        seen.add(field)
      }
      for (const [index, item] of value.entries()) if (!isBlock(item)) visit(item, `${field}.${index}`, false)
    } else if (value && typeof value === 'object' && (top || !isBlock(value))) {
      for (const [key, item] of Object.entries(value)) visit(item, key, false)
    }
  }
  visit(owner, '', true)
  return shared
}

function isBlock(value: unknown): value is PageBlock {
  return value !== null && typeof value === 'object' && !Array.isArray(value) &&
    'id' in value && typeof value.id === 'string' && 'type' in value && typeof value.type === 'string'
}

function isList(value: PageValue): value is readonly PageValue[] {
  return Array.isArray(value)
}

const interestingTypes = ['page', 'pageLink', 'question', 'cloze', 'img', 'video', 'audio', 'file'] as const
export type AffectedBlocks = { [K in typeof interestingTypes[number]]?: Extract<Block, { type: K }>[] }

export function getAffectedBlocks(delta: ViewDelta): { inserted: AffectedBlocks; deleted: AffectedBlocks; moved: BlockChange[]; updated: BlockChange[] } {
  const changes = classifyBlockDelta(delta)
  const replacements = changes.updated.filter(change => change.before.block.type !== change.after.block.type ||
    (change.after.block.type === 'question' || change.after.block.type === 'cloze') &&
    change.before.block.qid !== change.after.block.qid)
  const group = (locations: BlockLocation[]): AffectedBlocks => {
    const result: AffectedBlocks = {}
    for (const type of interestingTypes) {
      const blocks = locations.filter(location => location.block.type === type).map(location => location.block)
      if (blocks.length > 0) Object.assign(result, { [type]: blocks })
    }
    return result
  }
  return { inserted: group([...changes.inserted, ...replacements.map(change => change.after)]),
    deleted: group([...changes.deleted, ...replacements.map(change => change.before)]),
    moved: changes.moved, updated: changes.updated }
}

/** Longest preserved sibling order, O(n log n). Index shifts caused by insertion are not moves. */
function stableOrder(list: BlockLocation[], before: ReadonlyMap<string, BlockLocation>): string[] {
  const tails: number[] = []
  const previous: number[] = []
  const indices = list.map(location => (before.get(location.block.id) as BlockLocation).index)
  for (const [index, value] of indices.entries()) {
    let left = 0
    let right = tails.length
    while (left < right) {
      const middle = (left + right) >>> 1
      if (indices[tails[middle]] < value) left = middle + 1
      else right = middle
    }
    previous[index] = tails[left - 1] ?? -1
    tails[left] = index
  }
  const result: string[] = []
  for (let index = tails.at(-1) ?? -1; index >= 0; index = previous[index]) result.push(list[index].block.id)
  return result
}

function same(left: unknown, right: unknown): boolean {
  if (left === right) return true
  if (!left || !right || typeof left !== 'object' || typeof right !== 'object' ||
    Array.isArray(left) !== Array.isArray(right)) return false
  const a = left as Record<string, unknown>
  const b = right as Record<string, unknown>
  const keys = Object.keys(a)
  return keys.length === Object.keys(b).length && keys.every(key => Object.hasOwn(b, key) && same(a[key], b[key]))
}
